<script setup lang="ts">
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRoute } from 'vue-router'

import PointsChip from '@/components/gamification/PointsChip.vue'
import AppSidebar, { type NavItem } from '@/components/layout/AppSidebar.vue'
import AppTopbar from '@/components/layout/AppTopbar.vue'
import BaseButton from '@/components/ui/BaseButton.vue'
import { isStaff } from '@/lib/permissions'
import { useAuthStore } from '@/stores/auth'

const { t } = useI18n()
const route = useRoute()
const auth = useAuthStore()

const items = computed<NavItem[]>(() => {
  const guest = !auth.isAuthenticated
  return [
    { to: '/dashboard', labelKey: 'nav.dashboard', icon: 'dashboard', locked: guest },
    { to: '/courses', labelKey: 'nav.courses', icon: 'book' },
    { to: '/journeys', labelKey: 'nav.journeys', icon: 'route' },
    { to: '/certificates', labelKey: 'nav.certificates', icon: 'award', locked: guest },
  ]
})

const loginTo = computed(() => ({
  name: 'login' as const,
  query: { redirect: route.fullPath },
}))
const registerTo = computed(() => ({
  name: 'register' as const,
  query: { redirect: route.fullPath },
}))

</script>

<template>
  <div class="flex min-h-full flex-col bg-reybex-page dark:bg-neutral-900">
    <AppTopbar>
        <template #actions>
          <PointsChip v-if="auth.isAuthenticated" />
          <template v-else>
            <RouterLink
              :to="registerTo"
              class="text-sm font-medium text-gray-900 hover:text-gray-600 dark:text-neutral-100"
            >
              {{ t('auth.signUp') }}
            </RouterLink>
            <RouterLink
              :to="loginTo"
              class="rounded-lg bg-brand-600 px-3 py-2 text-sm font-medium text-white hover:bg-brand-700"
            >
              {{ t('auth.signIn') }}
            </RouterLink>
          </template>
        </template>
      </AppTopbar>

    <div class="flex min-h-0 flex-1">
    <AppSidebar :items="items" appearance="energy">
      <template v-if="isStaff(auth.role)" #footer>
        <BaseButton to="/admin/courses" size="sm" icon="shield" icon-aside block>
          {{ t('nav.adminArea') }}
        </BaseButton>
      </template>
    </AppSidebar>

    <div class="flex min-w-0 flex-1 flex-col">
      <a
        href="#main-content"
        class="sr-only focus:not-sr-only focus:absolute focus:z-50 focus:m-3 focus:rounded-lg focus:bg-white focus:px-4 focus:py-2 focus:text-sm focus:shadow"
      >
        {{ t('nav.skipToContent') }}
      </a>

      <main id="main-content" class="mx-auto w-full max-w-7xl flex-1 px-4 py-6 sm:px-6 lg:px-8">
        <slot />
      </main>
    </div>
    </div>
  </div>
</template>
